import { ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Loader2, X } from "lucide-react";

export interface BasicDialogProps {
  /** Controls dialog open/close state */
  open: boolean;
  /** Callback when dialog state changes */
  onOpenChange: (open: boolean) => void;

  /** Dialog title text */
  title?: ReactNode;
  /** Dialog description text */
  description?: ReactNode;
  /** Icon to display in title */
  icon?: ReactNode;

  /** Tone affects border and confirm button styling */
  tone?: "default" | "warning" | "destructive";

  /** Dismiss button (left side) - max 2 actions enforced */
  dismiss?: {
    label: string;
    onClick?: () => void;
  };
  /** Confirm button (right side) - max 2 actions enforced */
  confirm?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    loadingLabel?: string;
    variant?: "default" | "destructive";
  };

  /** Close on Escape key (default: true) */
  closeOnEsc?: boolean;
  /** Close on outside click (default: true, false for destructive) */
  closeOnOutsideClick?: boolean;

  /** Optional content */
  children?: ReactNode;
}

/**
 * Material 3 compliant BasicDialog component.
 * Enforced specs: 28px radius, 280-560px width, 24px padding, max 2 actions.
 */
export function BasicDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  tone = "default",
  dismiss,
  confirm,
  closeOnEsc = true,
  closeOnOutsideClick,
  children,
}: BasicDialogProps) {
  // Default closeOnOutsideClick to false for destructive, true otherwise
  const shouldCloseOnOutsideClick = closeOnOutsideClick ?? (tone !== "destructive");

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  const handlePointerDownOutside = (e: Event) => {
    if (!shouldCloseOnOutsideClick) {
      e.preventDefault();
    }
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    if (!closeOnEsc) {
      e.preventDefault();
    }
  };

  const isDestructive = tone === "destructive";
  const isWarning = tone === "warning";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          onPointerDownOutside={handlePointerDownOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          className={cn(
            // Material 3 specs: 28px radius, 280-560px width, 24px padding
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-full min-w-[280px] max-w-[560px]",
            "rounded-dialog p-6",
            "bg-background shadow-lg",
            "border",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            // M3 compliant: No colored borders for dialogs - use neutral border only
            "border-border"
          )}
        >
          {/* Header: Title + Description */}
          {(title || description) && (
            <div className="flex flex-col gap-4">
              {title && (
                <DialogPrimitive.Title
                  className={cn(
                    "flex items-center gap-2 text-lg font-semibold leading-none tracking-tight",
                    isDestructive && "text-destructive",
                    isWarning && "text-orange-600"
                  )}
                >
                  {icon}
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          )}

          {/* Content */}
          {children && (
            <div className={cn("mt-4", (title || description) && "mt-4")}>
              {children}
            </div>
          )}

          {/* Actions: 24px gap from body, 8px gap between buttons */}
          {(dismiss || confirm) && (
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
              {dismiss && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    dismiss.onClick?.();
                    if (!dismiss.onClick) {
                      onOpenChange(false);
                    }
                  }}
                >
                  {dismiss.label}
                </Button>
              )}
              {confirm && (
                <Button
                  type="button"
                  variant={confirm.variant === "destructive" ? "destructive" : "default"}
                  onClick={confirm.onClick}
                  disabled={confirm.disabled || confirm.loading}
                  className={cn(
                    isWarning && confirm.variant !== "destructive" && "bg-orange-600 hover:bg-orange-700 text-white"
                  )}
                >
                  {confirm.loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {confirm.loadingLabel || confirm.label}
                    </>
                  ) : (
                    confirm.label
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Close button (X) for accessibility */}
          <DialogPrimitive.Close
            className={cn(
              "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background",
              "transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
